/*
 * Copyright (c) 2021 - 2026 All rights reserved by Ferenc Farkas <wolferi93@gmail.com>
 */

/**
 * @file activeChatView.js
 * @description
 * Module-level registry of the Team Chat conversation currently on screen, so the
 * push layer (expo `setNotificationHandler` + the FCM foreground listener) can
 * drop the banner / sound of a chat message that lands in the very conversation
 * the user is reading. Lives outside React because the notification handler is
 * installed at module scope.
 */
import {AppState} from 'react-native';

let activeView = null;

/**
 * Register the conversation on screen (`null` clears it).
 * @param {{channelId: string, threadRootId?: (string|null)}|null} view
 * @returns {void}
 */
export const setActiveChatView = (view) => {
    activeView = view?.channelId
        ? {channelId: String(view.channelId), threadRootId: view.threadRootId ? String(view.threadRootId) : ''}
        : null;
};

/**
 * Clear the registry only if it still points at `view` (a newer screen may already own it).
 * @param {{channelId: string, threadRootId?: (string|null)}} view
 * @returns {void}
 */
export const clearActiveChatView = (view) => {
    if (activeView
        && activeView.channelId === String(view?.channelId || '')
        && activeView.threadRootId === String(view?.threadRootId || '')) {
        activeView = null;
    }
};

/**
 * Whether a push data payload is a chat message of the conversation currently
 * open in the foregrounded app (→ show nothing, the message is already visible).
 * @param {Object} data Push `data` (FCM) / notification content data (expo).
 * @returns {boolean}
 */
export const isPushForActiveChatView = (data) => {
    if (!activeView || !data || AppState.currentState !== 'active') {
        return false;
    }
    const type = String(data.type || '');
    const eventKey = String(data.eventKey || '');
    if (type !== 'chat' && !eventKey.startsWith('chat.message')) {
        return false;
    }

    return String(data.channelId || '') === activeView.channelId
        && String(data.threadRoot || '') === activeView.threadRootId;
};
